// Phase 11 slice 1 — Water Quality Monitor.
//
// Synthetic inline water probe readings: pH / EC / DO / ORP / water temp /
// turbidity. Seeded per course; ticks alongside the soil sim every 15 min.
// Idaho DEQ compliance check + bloom prediction land in slice 2.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { UserRole } from '@greeniq/shared';
import { ApiError, api } from '../api/client.js';
import { useMe } from '../context/MeContext.js';
import {
  DARK_CHART_TOKENS,
  darkTooltipContentStyle,
  darkTooltipItemStyle,
  darkTooltipLabelStyle,
} from '../lib/charts-theme';

interface ProbeData {
  sensorId: number;
  createdAt: string;
  latest: Record<string, { value: number; unit: string | null; ts: string }>;
  series: Array<{ metric: string; points: Array<{ ts: string; value: number }> }>;
}

interface WaterQualityResponse {
  probes: ProbeData[];
}

const METRIC_META: Record<string, { label: string; unit: string; range: [number, number]; color: string; hint: string }> = {
  water_ph: { label: 'pH', unit: 'pH', range: [6.0, 9.5], color: '#2563eb', hint: '6.5-8.5 healthy' },
  water_do_mgl: { label: 'DO', unit: 'mg/L', range: [0, 14], color: '#16a34a', hint: '≥5 mg/L healthy' },
  water_temp_f: { label: 'Water temp', unit: '°F', range: [32, 100], color: '#dc2626', hint: '' },
  water_ec: { label: 'EC', unit: 'dS/m', range: [0, 3], color: '#a16207', hint: '' },
  water_orp_mv: { label: 'ORP', unit: 'mV', range: [0, 500], color: '#7c3aed', hint: '+200 to +400 healthy' },
  water_turbidity_ntu: { label: 'Turbidity', unit: 'NTU', range: [0, 50], color: '#ea580c', hint: '<10 is clear' },
};

const METRIC_ORDER = [
  'water_do_mgl',
  'water_ph',
  'water_temp_f',
  'water_ec',
  'water_orp_mv',
  'water_turbidity_ntu',
];

export function WaterQualityPage() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const isAdmin = role === 'ageye_admin';

  const [data, setData] = useState<WaterQualityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    if (!courseSlug) return;
    try {
      const r = await api.get<WaterQualityResponse>(`/courses/${courseSlug}/water-quality`);
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  async function onSeed() {
    if (!courseSlug) return;
    setMessage(null);
    try {
      const r = await api.post<{ inserted: boolean }>(`/courses/${courseSlug}/seed-demo-water-probe`);
      setMessage(r.inserted ? 'Water probe seeded at course centroid.' : 'Probe already exists.');
      await load();
    } catch (err) {
      setMessage(`Seed failed: ${err instanceof ApiError ? err.message : String(err)}`);
    }
  }

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;
  if (!data) return <div className="p-8 text-fg-muted">Loading…</div>;

  const probe = data.probes[0] ?? null;

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Phase 11</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">Water Quality Monitor</h1>
          <p className="mt-1 text-fg-muted">
            Inline pond probe — pH, EC, DO, ORP, water temp, turbidity. Synthetic until Atlas Scientific inline kit deploys.
          </p>
        </div>
        {isAdmin && !probe && (
          <button
            onClick={() => void onSeed()}
            className="px-3 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-xs font-semibold hover:bg-greeniq-400"
          >
            Seed water probe
          </button>
        )}
      </div>

      {message && <div className="mt-4 p-3 rounded-md bg-bg-elevated text-sm text-fg">{message}</div>}

      {!probe && (
        <div className="mt-8 p-8 text-center text-fg-muted bg-bg-card rounded-lg border border-dashed border-border">
          <p>No water probe configured yet.</p>
          <p className="mt-2 text-sm">
            {isAdmin
              ? 'Click "Seed water probe" above to create one at the course centroid.'
              : 'Ask an AGEYE admin to seed the demo probe.'}
          </p>
        </div>
      )}

      {probe && (
        <>
          {/* Latest readings tile grid */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {METRIC_ORDER.map((metric) => {
              const meta = METRIC_META[metric]!;
              const latest = probe.latest[metric];
              return (
                <div key={metric} className="p-3 rounded-md bg-bg-card border border-border">
                  <div className="text-[10px] uppercase tracking-wider text-fg-muted">{meta.label}</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-2xl font-semibold" style={{ color: meta.color }}>
                      {latest ? latest.value.toFixed(metric === 'water_ec' ? 3 : 1) : '—'}
                    </span>
                    <span className="text-[10px] text-fg-muted">{meta.unit}</span>
                  </div>
                  {meta.hint && <div className="mt-1 text-[10px] text-fg-muted">{meta.hint}</div>}
                </div>
              );
            })}
          </div>

          {/* 7-day trend charts */}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {METRIC_ORDER.map((metric) => {
              const meta = METRIC_META[metric]!;
              const s = probe.series.find((x) => x.metric === metric);
              if (!s || s.points.length < 2) return null;
              const chartData = s.points.map((p) => ({
                ts: new Date(p.ts).toLocaleString(undefined, {
                  month: 'short', day: 'numeric', hour: 'numeric',
                }),
                value: p.value,
              }));
              return (
                <div key={metric} className="p-4 bg-bg-card rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-fg-muted font-semibold">
                      {meta.label} · 7d
                    </div>
                    <span className="text-[10px] text-fg-muted">{meta.unit}</span>
                  </div>
                  <div className="mt-2" style={{ width: '100%', height: 140 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 6, right: 6, left: 0, bottom: 6 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={DARK_CHART_TOKENS.gridStroke} />
                        <XAxis
                          dataKey="ts"
                          tick={{ fontSize: 9, fill: DARK_CHART_TOKENS.axisColor }}
                          stroke={DARK_CHART_TOKENS.axisColor}
                          minTickGap={40}
                        />
                        <YAxis
                          domain={meta.range}
                          tick={{ fontSize: 10, fill: DARK_CHART_TOKENS.axisColor }}
                          stroke={DARK_CHART_TOKENS.axisColor}
                          width={32}
                        />
                        <Tooltip
                          contentStyle={darkTooltipContentStyle}
                          itemStyle={darkTooltipItemStyle}
                          labelStyle={darkTooltipLabelStyle}
                          formatter={(v) => (typeof v === 'number' ? v.toFixed(2) : v)}
                        />
                        <Line type="monotone" dataKey="value" stroke={meta.color} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-6 text-xs text-fg-muted">
            Physics-simulated: water temp follows air-temp 24h mean with thermal lag; DO from Benson-Krause saturation × 0.90 pond deficit; pH diurnal from algal photosynthesis; ORP couples to DO; turbidity spikes with rainfall.
          </p>
        </>
      )}
    </div>
  );
}
